import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'

import Nickname from '@/components/nickname'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { formatNumber } from '@/lib/formatter'
import {
  useCompetitionBetMutation,
  useReadBettingUserOverviewQuery,
} from '@/redux/competition-betting'
import { RelatedSimpleUser } from '@/redux/user'
import { useAppSelector } from '@/redux/utils'

export interface BetDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  competitionId: number
  competitor: RelatedSimpleUser
}

export default function BetDialog({
  open,
  onOpenChange,
  competitionId,
  competitor,
}: BetDialogProps) {
  const token = useAppSelector((state) => state.global.token)
  const onClose = useCallback(() => onOpenChange(false), [onOpenChange])
  const [competitionBet, { isLoading }] = useCompetitionBetMutation()
  const { data: bettingUserOverviewData } = useReadBettingUserOverviewQuery(
    competitionId,
    { skip: token === undefined },
  )

  const [amount, setAmount] = useState('')

  useEffect(() => {
    setAmount('')
  }, [open])

  const isOverLimit = useMemo(() => {
    return +amount > (bettingUserOverviewData?.totalPoints ?? 0) * 0.2
  }, [amount, bettingUserOverviewData])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <VisuallyHidden>
        <DialogTitle>
          Bet on <Nickname className="inline" user={competitor} />
        </DialogTitle>
      </VisuallyHidden>
      <DialogContent className="w-120" showClose={false}>
        <div className="flex flex-col gap-6">
          <div className="text-[18px] font-semibold leading-6.5">
            Subscribe to <Nickname className="inline" user={competitor} />
          </div>
          <div>
            <div className="flex justify-between items-center">
              <div className="text-sm text-secondary-foreground leading-4.5">
                Subscription Amount
              </div>
              <div className="text-xs text-muted-foreground pr-2">
                2% of handing fee required
              </div>
            </div>
            <div className="relative mt-3">
              <Input
                className={'pr-10'}
                placeholder="Enter amount of points to subscribe"
                aria-invalid={isOverLimit}
                value={amount}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value
                  // 只允许数字输入，将非数字字符替换为空
                  const numericValue = value.replace(/[^0-9]/g, '')
                  setAmount(numericValue)
                }}
              ></Input>
              <div className="text-base text-muted-foreground absolute right-3 top-0 bottom-0 flex justify-end items-center">
                pts
              </div>
            </div>
            {/* <div
              className={clsx(
                "mt-3 h-12 px-3 flex justify-between items-center bg-white/4 border border-white/4 rounded-xl",
                isOverLimit ? "border-(--color-destructive)" : "border-white/4"
              )}
            >
              <input
                className="grow bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
                placeholder="Enter amount of points to subscribe"
                value={amount}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  // 只允许数字输入，将非数字字符替换为空
                  const numericValue = value.replace(/[^0-9]/g, "");
                  setAmount(numericValue);
                }}
              />
              <div className="text-base text-muted-foreground">pts</div>
            </div> */}
            {!isOverLimit ? (
              <div className="mt-2 text-xs text-muted-foreground leading-4.5">
                Subscription Range: 10 pts to{' '}
                {formatNumber()(
                  (bettingUserOverviewData?.totalPoints ?? 0) * 0.2,
                )}
                pts (20% limit of total points)
              </div>
            ) : (
              <div className="mt-2 text-xs text-(--color-destructive) leading-4.5">
                Each Subscription can be up to 20% of your current balance (
                {formatNumber()(
                  (bettingUserOverviewData?.totalPoints ?? 0) * 0.2,
                )}
                pts)
              </div>
            )}
          </div>
          <div className="flex justify-end items-center gap-2">
            <Button
              className="px-4 h-9 rounded-full font-semibold text-sm cursor-pointer"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              className="px-4 h-9 rounded-full font-semibold text-sm cursor-pointer"
              variant="primary"
              disabled={
                +amount < 10 ||
                +amount > (bettingUserOverviewData?.totalPoints ?? 0) * 0.2
              }
              isLoading={isLoading}
              onClick={async () => {
                if (!competitor) return
                await competitionBet({
                  competitionId,
                  competitorId: competitor.id,
                  amount: +amount,
                }).unwrap()
                onClose()
              }}
            >
              Subscribe
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
